import { Image, IImageProps } from '@msdyn365-commerce/core-internal';
import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Skeleton } from './skeleton/skeleton';

interface Props extends IImageProps {
    wrapperClassname?: string;
}

export const ImageWithLoading = ({ wrapperClassname, ...imageProps }: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (!imageProps.src) {
            setIsLoaded(true);
            return;
        }
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        let img: HTMLImageElement | null = null;

        const markLoaded = () => setIsLoaded(true);

        const check = () => {
            const found = wrapper.querySelector('img');

            if (!(found instanceof HTMLImageElement)) {
                return;
            }

            if (img !== found) {
                img = found;
                img.addEventListener('error', markLoaded);
            }

            if (found.complete && found.naturalWidth > 1) {
                setIsLoaded(true);
            }
        };

        const observer = new MutationObserver(check);

        observer.observe(wrapper, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src', 'srcset', 'class']
        });

        check();

        return () => {
            observer.disconnect();
            if (img) {
                img.removeEventListener('error', markLoaded);
            }
        };
    }, [imageProps.src]);

    return (
        <div ref={wrapperRef} className={classnames('image-with-loading', wrapperClassname)}>
            {!isLoaded && <Skeleton />}
            <Image {...imageProps} />
        </div>
    );
};
